import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, GitMerge, Play, RefreshCw, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <Card className="rounded-xl border-border shadow-none" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <CardContent className="p-4 flex items-center gap-3">
      <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></span>
      <div>
        <p className="text-2xl font-semibold leading-none">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function ProfileConflicts() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [merging, setMerging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/candidates/profile-merge/conflicts");
      setReport(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/candidates/profile-merge/conflicts?format=csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "profile_conflicts.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const runMerge = async () => {
    setMerging(true);
    try {
      const res = await api.post("/candidates/profile-merge/run");
      const r = res.data;
      toast.success(`Merge complete — ${r.filled} blanks filled, ${r.appended} added to lists, ${r.candidates_updated} candidates updated, ${r.conflicts_skipped} conflicts skipped`);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setMerging(false);
    }
  };

  const s = report?.summary || {};

  return (
    <div className="space-y-6" data-testid="profile-conflicts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><GitMerge className="h-6 w-6" /> Profile Conflicts</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Read-only review of where the legacy Recruitment Profile disagrees with Candidate Master.
            No data is changed here. Review conflicts before the merge is run.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-lg" data-testid="refresh-report-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={downloadCsv} disabled={downloading || !report} variant="outline" className="rounded-lg" data-testid="download-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Download CSV
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={merging || !report} className="rounded-lg" data-testid="run-merge-btn">
                <Play className="h-4 w-4 mr-2" /> {merging ? "Merging…" : "Run Merge"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="run-merge-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Run the profile merge?</AlertDialogTitle>
                <AlertDialogDescription>
                  This fills <strong>{s.auto_fills ?? 0}</strong> blank Candidate Master field(s) from the legacy
                  Recruitment Profiles and archives them. Existing values are never overwritten, and the
                  {" "}{s.conflicts ?? 0} listed conflict(s) are left untouched for manual review. This is safe to
                  run once; re-running changes nothing further.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="run-merge-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={runMerge} data-testid="run-merge-confirm-btn">Run Merge</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Users} label="Recruitment Profiles" value={s.recruitment_profiles ?? 0} tone="bg-primary/10 text-primary" />
            <Stat icon={AlertTriangle} label="Conflicts" value={s.conflicts ?? 0} tone="bg-red-100 text-red-600" />
            <Stat icon={GitMerge} label="Auto-fills" value={s.auto_fills ?? 0} tone="bg-emerald-100 text-emerald-600" />
            <Stat icon={Users} label="Orphan Profiles" value={s.orphans ?? 0} tone="bg-amber-100 text-amber-600" />
          </div>

          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" /> Conflicts — both systems have a value and they differ
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(report?.conflicts || []).length === 0 ? (
                <EmptyState title="No conflicts" description="No candidate has differing values across the two systems. The merge will only fill blanks." />
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="conflicts-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidate</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Candidate Directory</TableHead>
                        <TableHead>Recruitment Profile</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.conflicts.map((c, i) => (
                        <TableRow key={i} data-testid={`conflict-row-${i}`}>
                          <TableCell className="font-medium">{c.candidate_name} <span className="font-mono text-xs text-muted-foreground">{c.candidate_code}</span></TableCell>
                          <TableCell className="font-mono text-xs">{c.mobile}</TableCell>
                          <TableCell><Badge variant="outline" className="rounded-full">{c.field}</Badge></TableCell>
                          <TableCell className="text-red-600">{String(c.candidate_value)}</TableCell>
                          <TableCell className="text-emerald-700">{String(c.recruitment_profile_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <GitMerge className="h-4 w-4 text-emerald-600" /> Auto-fills — Candidate Master is blank, will be filled from the profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(report?.auto_fills || []).length === 0 ? (
                <EmptyState title="Nothing to auto-fill" description="No blank Candidate Master fields would be filled from the recruitment profiles." />
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="autofills-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidate</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Value to fill</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.auto_fills.map((f, i) => (
                        <TableRow key={i} data-testid={`autofill-row-${i}`}>
                          <TableCell className="font-medium">{f.candidate_name} <span className="font-mono text-xs text-muted-foreground">{f.candidate_code}</span></TableCell>
                          <TableCell><Badge variant="outline" className="rounded-full">{f.field}</Badge></TableCell>
                          <TableCell>{String(f.recruitment_profile_value)}</TableCell>
                          <TableCell><span className="text-xs text-muted-foreground">{f.action === "append_to_list" ? "add to list" : "fill blank"}</span></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
